/**
 * Field suggestions for funding application forms
 * Based on FairCare's business model, services, and value proposition
 */

export interface FieldSuggestion {
  field: string;
  suggestion: string;
  description: string;
}

export const fundingFieldSuggestions: Record<string, FieldSuggestion> = {
  useOfFunds: {
    field: 'useOfFunds',
    description: 'How the funds will be used for FairCare operations',
    suggestion: `The requested funds will be used for critical operational expenses to scale FairCare's cash-pay-first health benefits platform:

1. **Cloud Infrastructure & Hosting** ($X/month): Firebase Blaze plan for production database, storage, and cloud functions to support member receipt uploads, OCR processing, and reimbursement calculations. This is essential for handling employer and member data securely.

2. **AI & Development Tools** ($X/month): Cursor Ultra and ChatGPT Pro subscriptions for rapid development of new features, automated receipt processing with OCR, fraud detection algorithms, and customer support automation.

3. **Security & Compliance** ($X/month): HIPAA-aligned security tools, encryption services, audit logging systems, and compliance monitoring to ensure member health data is protected.

4. **Core SaaS Subscriptions** ($X/month): Essential business tools including payment processing, email services, analytics platforms, and customer relationship management systems.

5. **Professional Services** ($X): Legal consultation for healthcare compliance, accounting services, and technical consulting for platform optimization.

6. **Working Capital** ($X): Cash flow to cover operational expenses during the initial growth phase as we onboard small employers (10-50 employees) seeking affordable healthcare benefits.

These funds will directly support our mission to provide transparent, cash-pay healthcare benefits that eliminate insurance pricing games and reduce costs for small businesses.`
  },

  executiveSummary: {
    field: 'executiveSummary',
    description: 'Compelling executive summary highlighting FairCare\'s value proposition',
    suggestion: `FairCare is a comprehensive healthcare benefits platform that revolutionizes how small employers provide health benefits to their employees. We eliminate the complexity and high costs of traditional insurance by using cash-pay pricing and simple reimbursement systems.

**The Problem:** Small businesses (10-50 employees) struggle to offer competitive healthcare benefits due to exorbitant insurance premiums, complex claims processes, and lack of transparency in pricing. Traditional insurance often costs $500-1,500+ per employee per month, making it unaffordable for many small employers.

**Our Solution:** FairCare provides a cash-pay-first health benefits ecosystem where employers fund benefit plans with clear category rules (chiropractic, primary care, mental health, dental, pharmacy, telehealth, etc.). Employees pay cash prices directly to providers (which are typically 40-60% lower than insurance rates), then receive automated reimbursements through our platform. Our technology includes OCR receipt processing, automated categorization, fraud detection, and real-time balance tracking.

**Target Market:** Small employers in Minnesota and beyond who want to offer meaningful healthcare benefits without the cost and complexity of traditional insurance. We focus on businesses that value transparency, cost savings, and employee wellness.

**Business Model:** We generate revenue through monthly SaaS subscriptions from employers, typically $50-200 per employer per month based on company size. Additional revenue streams include provider network partnerships and premium features.

**Why We Need Funding:** We need working capital to scale our cloud infrastructure, enhance our AI-powered receipt processing, expand our provider network, and support customer acquisition as we onboard new employers. This funding will accelerate our growth and help us reach profitability within [X] months.`
  },

  marketAnalysis: {
    field: 'marketAnalysis',
    description: 'Market opportunity and competitive landscape analysis',
    suggestion: `**Market Size & Opportunity:**
The U.S. small business healthcare benefits market is massive and growing. There are over 30 million small businesses in the U.S., with approximately 5.8 million having 10-50 employees. The small business health insurance market is valued at over $150 billion annually, with average premiums of $7,000-12,000 per employee per year.

**Market Trends:**
- Small businesses are increasingly frustrated with traditional insurance costs and complexity
- Cash-pay healthcare is growing 15-20% annually as consumers seek transparent pricing
- Employers are looking for alternatives to traditional insurance that offer better value
- The shift to remote work has increased demand for flexible, portable benefits
- Telehealth adoption has accelerated, creating opportunities for digital-first benefit platforms

**Target Market:**
Our primary target is small employers (10-50 employees) in Minnesota and surrounding states, particularly:
- Service-based businesses (restaurants, retail, professional services)
- Technology companies
- Non-profits
- Manufacturing companies
- Healthcare practices

These businesses typically spend $6,000-10,000 per employee annually on health insurance, but struggle with high deductibles, limited networks, and administrative burden.

**Competitive Landscape:**
Traditional competitors include health insurance brokers and carriers (Blue Cross, UnitedHealthcare, etc.) who offer complex, expensive plans. Direct competitors include:
- Health reimbursement arrangements (HRAs) - but these lack our technology and automation
- Health savings accounts (HSAs) - but these require high-deductible insurance plans
- Direct primary care (DPC) - but this is limited to primary care only

**Our Competitive Advantage:**
FairCare combines the simplicity of cash-pay pricing with modern technology (OCR, automation, fraud detection) and a comprehensive benefit structure covering multiple care categories. We're the only platform that offers a complete cash-pay benefits ecosystem with employer-funded plans, automated reimbursements, and provider network integration.`
  },

  competitiveAdvantage: {
    field: 'competitiveAdvantage',
    description: 'What makes FairCare unique and defensible',
    suggestion: `FairCare's competitive advantages create significant barriers to entry and sustainable differentiation:

**1. Technology-First Approach:**
- Automated OCR receipt processing eliminates manual claims submission
- Real-time balance tracking and reimbursement calculations
- Fraud detection algorithms that protect employer funds
- Mobile-first member experience with instant receipt upload
- Employer dashboard with comprehensive analytics and spend tracking

**2. Cash-Pay Pricing Advantage:**
- Members pay 40-60% less than insurance rates by using cash pricing
- No network restrictions - members can see any provider
- Transparent pricing eliminates surprise bills
- Providers prefer cash payments (faster, no claims processing)

**3. Comprehensive Benefit Structure:**
- Covers 8+ care categories (chiropractic, primary care, mental health, dental, pharmacy, telehealth, labs, imaging)
- Flexible plan configuration allows employers to customize benefits
- Category-specific rules and visit limits provide cost control
- Family and individual allocation options

**4. Employer Cost Savings:**
- Employers typically save 30-50% compared to traditional insurance premiums
- Predictable costs with clear category allowances
- No surprise rate increases or complex claims processing
- Simple administration through our employer dashboard

**5. First-Mover Advantage:**
- We're building the first comprehensive cash-pay benefits platform with full automation
- Early customer relationships and feedback shape product development
- Network effects as we add more providers and employers
- Data advantage from processing thousands of receipts and reimbursements

**6. Regulatory & Compliance Expertise:**
- HIPAA-aligned data handling and security
- Full audit logging for compliance
- Healthcare industry knowledge and relationships
- Understanding of employer benefit regulations

**7. Scalable Business Model:**
- Low marginal costs (SaaS model)
- High customer lifetime value
- Recurring revenue from monthly subscriptions
- Potential for additional revenue streams (provider network fees, premium features)`
  },

  businessModel: {
    field: 'businessModel',
    description: 'How FairCare generates revenue',
    suggestion: `FairCare operates on a B2B SaaS subscription model with multiple revenue streams:

**Primary Revenue Stream: Monthly Employer Subscriptions**
- Base subscription: $50-200 per employer per month (based on company size)
- Pricing tiers based on number of employees (10-20, 21-35, 36-50)
- Includes: Platform access, member management, benefit plan configuration, reimbursement processing, employer dashboard, customer support
- Average customer lifetime value: $6,000-24,000 (assuming 3-5 year retention)

**Secondary Revenue Streams:**
1. **Provider Network Partnerships**: Preferred provider network with negotiated cash rates
   - Providers pay small listing fees or revenue share on referrals
   - Creates value for both providers (more patients) and members (better rates)

2. **Premium Features**: Advanced analytics, custom reporting, API access
   - Additional $20-50/month per employer for premium features
   - Targets larger employers who need more sophisticated tools

3. **Transaction Fees** (Future): Small per-transaction fee for reimbursements processed
   - Optional: $0.50-1.00 per reimbursement processed
   - Only if employers want to offset platform costs

**Customer Acquisition:**
- Direct sales to small employers through targeted outreach
- Partnerships with business associations, chambers of commerce
- Referral program from existing customers
- Digital marketing targeting "affordable employee benefits" keywords

**Unit Economics:**
- Customer acquisition cost (CAC): $500-1,500 per employer
- Monthly recurring revenue (MRR): $50-200 per employer
- Gross margin: 75-85% (primarily SaaS, low marginal costs)
- Payback period: 3-6 months
- Target: 100+ employers in Year 1, 500+ in Year 2

**Revenue Projections:**
- Month 6: $5,000-10,000 MRR (25-50 employers)
- Month 12: $20,000-40,000 MRR (100-200 employers)
- Month 24: $50,000-100,000 MRR (250-500 employers)

**Path to Profitability:**
With this funding, we project reaching profitability (positive cash flow) within 12-18 months as we scale to 100+ employers and optimize our operations.`
  },

  targetCustomers: {
    field: 'targetCustomers',
    description: 'Specific target customer profile',
    suggestion: `FairCare's target customers are small employers who are frustrated with traditional health insurance but want to offer competitive benefits to attract and retain employees.

**Primary Target: Small Employers (10-50 employees)**
- **Geographic Focus**: Initially Minnesota and surrounding states (Wisconsin, Iowa, North Dakota, South Dakota)
- **Industries**: Service-based businesses, professional services, retail, restaurants, technology companies, non-profits, healthcare practices, manufacturing
- **Pain Points**: 
  - High insurance premiums ($500-1,500+ per employee/month)
  - Complex claims processes and administrative burden
  - Limited provider networks
  - Lack of transparency in pricing
  - Difficulty competing with larger companies for talent

**Ideal Customer Profile:**
- Currently spending $6,000-12,000 per employee annually on health insurance
- Values transparency and cost control
- Wants to offer meaningful benefits without breaking the bank
- Tech-savvy enough to use our platform (or has HR/admin support)
- Located in areas with good cash-pay provider availability
- Has 10-50 employees (sweet spot for our pricing model)

**Customer Segments:**
1. **Cost-Conscious Employers**: Currently uninsured or offering minimal benefits due to cost
2. **Insurance-Frustrated Employers**: Have insurance but unhappy with costs/complexity
3. **Growth-Minded Employers**: Expanding and need scalable benefit solutions
4. **Values-Driven Employers**: Want transparent, employee-friendly benefits

**Why They Choose FairCare:**
- Save 30-50% compared to traditional insurance
- Transparent, predictable costs
- Better employee experience (cash pricing, no network restrictions)
- Simple administration through our platform
- Comprehensive coverage across multiple care categories
- Technology that automates the reimbursement process`
  },

  salesStrategy: {
    field: 'salesStrategy',
    description: 'How FairCare will acquire customers',
    suggestion: `FairCare's sales strategy focuses on direct outreach, partnerships, and digital marketing to reach small employers:

**1. Direct Sales & Outreach**
- **Targeted Outreach**: Identify small employers (10-50 employees) in target markets through business directories, LinkedIn, and local business associations
- **Sales Process**: 
  - Initial outreach via email/phone introducing FairCare
  - Discovery call to understand their current benefits situation and pain points
  - Customized proposal showing cost savings vs. current insurance
  - Demo of platform (employer dashboard, member app)
  - Pilot program with 1-2 employers to prove value
  - Full rollout and onboarding

**2. Partnership Channels**
- **Business Associations**: Partner with local chambers of commerce, industry associations, and small business networks
- **Benefits Brokers**: Work with independent benefits brokers who serve small businesses
- **Professional Services**: Partner with accountants, HR consultants, and business advisors who work with small employers
- **Provider Networks**: Collaborate with cash-pay providers who can refer employers to us

**3. Digital Marketing**
- **Content Marketing**: Blog posts, case studies, and resources about "affordable employee benefits" and "alternatives to health insurance"
- **SEO**: Target keywords like "small business health benefits," "affordable employee benefits," "cash-pay health benefits"
- **Social Media**: LinkedIn and Facebook advertising targeting small business owners and HR managers
- **Webinars**: Educational webinars about cash-pay benefits and cost savings

**4. Referral Program**
- Incentivize existing customers to refer other employers
- Offer discounts or credits for successful referrals
- Create case studies and testimonials from satisfied customers

**5. Pilot Programs**
- Start with 2-3 pilot employers to refine the product and prove value
- Use pilot results as social proof and case studies
- Iterate based on feedback before full market launch

**Sales Timeline:**
- Months 1-3: Pilot with 2-3 employers, refine product
- Months 4-6: Direct sales push, target 25-50 employers
- Months 7-12: Scale through partnerships and marketing, reach 100+ employers
- Year 2: Expand to 250-500 employers through multiple channels

**Key Metrics:**
- Conversion rate: 10-15% of qualified leads
- Sales cycle: 30-60 days from first contact to signed contract
- Customer acquisition cost: $500-1,500 per employer
- Monthly churn target: <2%`
  },

  repaymentPlan: {
    field: 'repaymentPlan',
    description: 'How FairCare will repay the loan',
    suggestion: `FairCare will repay this loan through revenue generated from monthly employer subscriptions and operational cash flow:

**Revenue-Based Repayment:**
- Primary repayment source: Monthly recurring revenue (MRR) from employer subscriptions ($50-200 per employer/month)
- As we onboard employers, MRR grows, providing increasing cash flow to service debt
- Target: 100+ employers within 12 months = $5,000-20,000+ MRR

**Repayment Timeline & Milestones:**
- **Months 1-3**: Use funds for infrastructure and operations; minimal revenue, focus on product development
- **Months 4-6**: Begin onboarding first employers; generate $2,000-5,000 MRR; start making interest payments
- **Months 7-12**: Scale to 50-100 employers; generate $5,000-20,000 MRR; make regular principal + interest payments
- **Months 13-24**: Reach 100-200 employers; generate $10,000-40,000 MRR; accelerate repayment
- **Months 25-36**: Scale to 200-500 employers; generate $20,000-100,000 MRR; complete loan repayment

**Specific Repayment Ability:**
- With 50 employers at $100/month average = $5,000 MRR = $60,000 annual revenue
- After operational costs (~$3,000/month = $36,000/year), we have $24,000/year for debt service
- With 100 employers = $10,000 MRR = $120,000 annual revenue
- After operational costs (~$5,000/month = $60,000/year), we have $60,000/year for debt service
- This provides strong ability to service debt payments of $X/month

**Revenue Triggers:**
- **Pilot Employers**: First 2-3 employers provide proof of concept and initial revenue
- **First 10 Employers**: Validates product-market fit and generates $1,000-2,000 MRR
- **50 Employers**: Reaches critical mass for sustainable operations and $5,000-10,000 MRR
- **100 Employers**: Achieves profitability and strong debt service capacity

**Risk Mitigation:**
- Conservative revenue projections (assuming slower growth than best case)
- Multiple revenue streams (subscriptions + partnerships + premium features)
- Low customer acquisition costs ensure positive unit economics
- High customer retention (health benefits are sticky, employers don't switch frequently)
- Scalable model means marginal costs are low as we grow

**Collateral (if required):**
- Business assets: Technology platform, customer relationships, brand
- Personal guarantee from founder (if required by lender)
- Future revenue streams as security

We are confident in our ability to repay this loan based on our proven business model, clear path to revenue, and conservative financial projections.`
  },

  revenueProjections: {
    field: 'revenueProjections',
    description: 'Detailed revenue projections for next 12-24 months',
    suggestion: `**Revenue Projections - Next 24 Months**

**Assumptions:**
- Average subscription: $100/month per employer (blended average across size tiers)
- Customer acquisition: Conservative growth trajectory
- Churn rate: 2% monthly (health benefits are sticky)
- Expansion revenue: 10% of customers add premium features

**Monthly Revenue Projections:**

**Months 1-3 (Pilot Phase):**
- Month 1: $0 (product development, no customers)
- Month 2: $200 (2 pilot employers @ $100/month)
- Month 3: $500 (5 employers @ $100/month)
- **Q1 Total: $700**

**Months 4-6 (Early Growth):**
- Month 4: $1,000 (10 employers)
- Month 5: $1,500 (15 employers)
- Month 6: $2,500 (25 employers)
- **Q2 Total: $5,000**

**Months 7-9 (Acceleration):**
- Month 7: $3,500 (35 employers)
- Month 8: $5,000 (50 employers)
- Month 9: $7,000 (70 employers)
- **Q3 Total: $15,500**

**Months 10-12 (Scale):**
- Month 10: $9,000 (90 employers)
- Month 11: $11,000 (110 employers)
- Month 12: $13,000 (130 employers)
- **Q4 Total: $33,000**
- **Year 1 Total: $54,200**

**Year 2 (Months 13-24):**
- Month 13: $15,000 (150 employers)
- Month 18: $25,000 (250 employers)
- Month 24: $40,000 (400 employers)
- **Year 2 Total: ~$300,000+**

**Key Milestones:**
- **Month 6**: $2,500 MRR = $30,000 annual run rate
- **Month 12**: $13,000 MRR = $156,000 annual run rate
- **Month 18**: $25,000 MRR = $300,000 annual run rate
- **Month 24**: $40,000 MRR = $480,000 annual run rate

**Revenue Breakdown:**
- 90% from monthly employer subscriptions
- 8% from premium features
- 2% from provider partnerships and other sources

**Path to Profitability:**
- Break-even: Month 8-10 (50-70 employers, $5,000-7,000 MRR)
- Profitable: Month 12+ (100+ employers, $10,000+ MRR)
- Strong profitability: Month 18+ (200+ employers, $20,000+ MRR)

These projections are conservative and assume steady, sustainable growth. With proper funding and execution, we believe we can exceed these targets.`
  },

  founderExperience: {
    field: 'founderExperience',
    description: 'Founder background and qualifications',
    suggestion: `[Founder Name] brings [X] years of experience in [relevant fields] and a deep understanding of both healthcare and technology:

**Professional Background:**
- [Previous roles, companies, achievements]
- [Relevant industry experience]
- [Technical skills and expertise]

**Healthcare Industry Knowledge:**
- Understanding of healthcare benefits, insurance, and reimbursement systems
- Experience with [specific healthcare experience if applicable]
- Knowledge of regulatory requirements (HIPAA, employer benefits)

**Technology & Business Expertise:**
- [Technical skills: software development, SaaS, cloud infrastructure]
- Experience building and scaling technology platforms
- Understanding of B2B SaaS business models and customer acquisition

**Why I'm Qualified to Run FairCare:**
- Passion for solving the healthcare affordability problem for small businesses
- Technical ability to build and maintain the platform
- Business acumen to acquire customers and manage operations
- Commitment to long-term success and sustainable growth
- Personal investment in the company's success

**Relevant Achievements:**
- [List specific achievements, awards, or milestones]
- [Previous business experience or startup experience]
- [Education or certifications if relevant]

I am fully committed to FairCare's success and have the skills, experience, and determination to build a sustainable, profitable business that helps small employers provide affordable healthcare benefits to their employees.`
  }
};

/**
 * Get suggestion for a specific field
 */
export function getFieldSuggestion(fieldName: string): FieldSuggestion | null {
  return fundingFieldSuggestions[fieldName] || null;
}

/**
 * Get all available field suggestions
 */
export function getAllSuggestions(): Record<string, FieldSuggestion> {
  return fundingFieldSuggestions;
}

